// Access ends are exact instants (FEATURES D1.3 / D1.14): staff see them as a
// date AND a time in Cairo, e.g. "Sat 31 Oct 2026, 11:00 Cairo time". Egypt
// has summer time again (2026: it ends on the night of 29 Oct), so every
// conversion goes through Intl with Africa/Cairo — never the browser's zone.
// The same rules as the API (entitlements/entitlement-rules.ts).

const TZ = 'Africa/Cairo';
const DAY_MS = 24 * 60 * 60 * 1000;

const labelFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const toMs = (d: Date | string | number | null | undefined): number | null => {
  if (d === null || d === undefined || d === '') return null;
  const t = new Date(d as any).getTime();
  return Number.isFinite(t) ? t : null;
};

function parts(ms: number): Record<string, string> {
  const p: Record<string, string> = {};
  labelFmt.formatToParts(new Date(ms)).forEach((x) => {
    p[x.type] = x.value;
  });
  return p;
}

const text = (p: Record<string, string>) =>
  `${p.weekday} ${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} Cairo time`;

/** A start instant: "Thu 1 Oct 2026, 12:00 Cairo time". */
export function cairoStartLabel(d: Date | string | number | null | undefined): string {
  const t = toMs(d);
  return t === null ? '—' : text(parts(t));
}

/**
 * An END instant (exclusive). One that falls on the first instant of a Cairo
 * day is "the end of the day before" and reads 23:59 of that day ("until 29 Oct").
 */
export function cairoEndLabel(d: Date | string | number | null | undefined): string {
  const t = toMs(d);
  if (t === null) return 'Lifetime';
  let p = parts(t);
  // The first instant of a Cairo day (00:00, or 01:00 when summer time starts
  // at midnight) is the end of the day before.
  if (parts(t - 1).day !== p.day) p = parts(t - 60000);
  return text(p);
}

/** Cairo's UTC offset (ms) at `utcMs`. */
function offsetMs(utcMs: number): number {
  const p: Record<string, number> = {};
  partsFmt.formatToParts(new Date(utcMs)).forEach((x) => {
    if (x.type !== 'literal') p[x.type] = Number(x.value);
  });
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return wall - (utcMs - (utcMs % 1000));
}

/** The first instant of calendar day y-m-d in Cairo (DST-aware). */
function cairoMidnight(y: number, m: number, d: number): number {
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - offsetMs(guess);
  return guess - offsetMs(first);
}

/** "YYYY-MM-DD" typed by staff -> the next 00:00 Cairo (the exclusive end of that day). */
export function untilEndOfCairoDay(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return null;
  const next = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + DAY_MS);
  return new Date(cairoMidnight(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()));
}

/** A stored calendar date (<date>T00:00:00.000Z) -> 00:00 Cairo of that day. */
export function cairoDayStart(stored: Date | string | null | undefined): Date | null {
  const t = toMs(stored as any);
  if (t === null) return null;
  const d = new Date(t);
  return new Date(cairoMidnight(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()));
}

/** Today's date in Cairo as "YYYY-MM-DD" (the min of a date input). */
export function cairoToday(now: Date = new Date()): string {
  const t = now.getTime();
  return new Date(t + offsetMs(t)).toISOString().slice(0, 10);
}

export const addDays = (from: Date | number, days: number): Date =>
  new Date(new Date(from).getTime() + days * DAY_MS);
